<template>
  <div class="success-container">
    <div class="success-card">
      <div class="icon-circle">✔</div>

      <h2 class="success-title">Booking Successful!</h2>
      <p class="success-subtitle">Thank you for your purchase</p>

      <p class="scan-note" v-if="allTickets.length">
        Scan the QR at the counter to collect your {{ ticketTypeText }}
      </p>

      <!-- SINGLE ticket (movie or fnb-only): old layout, big QR on top -->
      <div class="qr-section" v-if="allTickets.length === 1">
        <img
          v-if="allTickets[0].qrCodeUrl"
          :src="allTickets[0].qrCodeUrl"
          alt="QR Code"
          class="qr-code"
        />
        <p class="booking-code-text">{{ allTickets[0].bookingCode || "" }}</p>

        <div class="payment-info final-summary" v-if="allTickets[0].bookingDetails">
          <div class="final-summary-row">
            <span class="label">Movie:</span>
            <span class="value">
              {{ allTickets[0].bookingDetails.movie_title }} |
              {{ allTickets[0].bookingDetails.lang_name }} |
              {{ allTickets[0].bookingDetails.mf_name }}
            </span>
          </div>

          <div class="final-summary-row">
            <span class="label">Show:</span>
            <span class="value">{{ formatShowLine(allTickets[0].bookingDetails) }}</span>
          </div>

          <div class="final-summary-row">
            <span class="label">Screen:</span>
            <span class="value">{{ allTickets[0].bookingDetails.screen_name }}</span>
          </div>

          <div class="final-summary-row">
            <span class="label">Seats:</span>
            <span class="value">
              {{
                allTickets[0].bookingDetails.seats &&
                allTickets[0].bookingDetails.seats.length
                  ? allTickets[0].bookingDetails.seats.join(", ")
                  : ""
              }}
            </span>
          </div>
        </div>
      </div>

      <!-- MULTIPLE tickets: one row per movie/ticket, QR inline with details -->
      <div class="tickets-list" v-else-if="allTickets.length > 1">
        <div
          v-for="(ticket, ticketIndex) in allTickets"
          :key="'ticket-' + ticketIndex + '-' + (ticket.bookingCode || '')"
          class="ticket-row"
        >
          <div class="qr-inline">
            <img
              v-if="ticket.qrCodeUrl"
              :src="ticket.qrCodeUrl"
              alt="QR Code"
              class="qr-code-sm"
            />
            <span class="booking-code-text">{{ ticket.bookingCode || "" }}</span>
          </div>

          <div class="ticket-details" v-if="ticket.bookingDetails">
            <div class="detail-row">
              <span class="label">Movie:</span>
              <span class="value">
                {{ ticket.bookingDetails.movie_title }} |
                {{ ticket.bookingDetails.lang_name }} |
                {{ ticket.bookingDetails.mf_name }}
              </span>
            </div>

            <div class="detail-row">
              <span class="label">Show:</span>
              <span class="value">{{ formatShowLine(ticket.bookingDetails) }}</span>
            </div>

            <div class="detail-row">
              <span class="label">Screen:</span>
              <span class="value">{{ ticket.bookingDetails.screen_name }}</span>
            </div>

            <div class="detail-row">
              <span class="label">Seats:</span>
              <span class="value">
                {{
                  ticket.bookingDetails.seats && ticket.bookingDetails.seats.length
                    ? ticket.bookingDetails.seats.join(", ")
                    : ""
                }}
              </span>
            </div>
          </div>

          <!-- FnB-only ticket (no movie details attached to this row) -->
          <div class="ticket-details" v-else>
            <div class="detail-row">
              <span class="label">Food &amp; Beverage</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Payment Info -->
      <div class="payment-info final-summary">
        <div class="final-summary-row" v-if="FnbItems && FnbItems.length">
          <span class="label">F&amp;B:</span>
          <span class="value">{{ formatFnbLine() }}</span>
        </div>

        <p class="d-flex justify-content-between final-amt-text">
          <strong>Total Paid:</strong>
          <strong>{{ formatTotal() }}</strong>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import QRCode from "qrcode";

export default {
  name: "BookingSuccess",
  props: {
    BookingDetails: { type: Object },
    bookingCode: String,
    yourTicket: String,
    finalAmountPaid: String,
    FnbItems: Array,
    curr_code: String,
    // Array of { bookingCode, yourTicket, bookingDetails } for additional movies
    extraTickets: { type: Array, default: () => [] },
  },
  data() {
    return {
      ticketQrMap: {},
    };
  },
  computed: {
    grossAmount() {
      return parseFloat(this.finalAmountPaid || 0).toFixed(2);
    },
    displayCurrency() {
      return (
        this.curr_code ||
        (this.$store &&
          this.$store.getters &&
          this.$store.getters.GET_CURRENCY) ||
        (this.$store &&
          this.$store.getters &&
          this.$store.getters.getFnBCurrency &&
          this.$store.getters.getFnBCurrency.curr_code) ||
        ""
      );
    },
    ticketTypeText() {
      const hasFnb = Array.isArray(this.FnbItems) && this.FnbItems.length;
      const hasMovie = Boolean(this.BookingDetails);
      const hasExtra =
        Array.isArray(this.extraTickets) && this.extraTickets.length > 0;

      if (hasFnb && !hasMovie && !hasExtra) return "food";
      if (hasExtra && hasFnb) return "tickets & food";
      if (hasExtra) return "tickets";
      if (hasFnb && hasMovie) return "ticket & food";
      if (!hasFnb && hasMovie) return "ticket";
      return "";
    },
    allTickets() {
      const tickets = [];
      if (this.bookingCode || this.BookingDetails || this.yourTicket) {
        tickets.push({
          bookingCode: this.bookingCode,
          yourTicket: this.yourTicket,
          bookingDetails: this.BookingDetails,
          qrCodeUrl: this.ticketQrMap[this.bookingCode] || null,
        });
      }
      (this.extraTickets || []).forEach((extra) => {
        if (!extra) return;
        tickets.push({
          bookingCode: extra.bookingCode,
          yourTicket: extra.yourTicket,
          bookingDetails: extra.bookingDetails,
          qrCodeUrl: this.ticketQrMap[extra.bookingCode] || null,
        });
      });
      return tickets;
    },
  },
  watch: {
    yourTicket: {
      immediate: true,
      handler() {
        this.generateAllQrCodes();
      },
    },
    extraTickets: {
      deep: true,
      handler() {
        this.generateAllQrCodes();
      },
    },
  },
  mounted() {
    this.generateAllQrCodes();
  },
  methods: {
    formatShowLine(details) {
      if (!details || !details.og_show_date) return "";
      const day = moment(new Date(details.og_show_date)).format("ddd");
      const date = moment(new Date(details.og_show_date)).format("Do MMMM");
      const time = details.og_show_time || "";
      return `${day} | ${date} | ${time}`;
    },
    formatFnbLine() {
      if (!this.FnbItems || !this.FnbItems.length) return "";
      return this.FnbItems.map(
        (item) =>
          `${item.qty || item.fst_quantity || item.quantity || 1}x-${
            item.title || item.fst_item_name || item.item_name
          }`
      ).join(", ");
    },
    formatTotal() {
      const code = this.displayCurrency ? `${this.displayCurrency} ` : "";
      return `${code}${this.currFormat(this.grossAmount || 0)}`;
    },
    generateQr(url, bookingCode) {
      if (!url || !bookingCode) return;
      QRCode.toDataURL(String(url), { width: 140 }, (err, dataUrl) => {
        if (!err && dataUrl) this.$set(this.ticketQrMap, bookingCode, dataUrl);
      });
    },
    generateAllQrCodes() {
      setTimeout(() => {
        if (this.yourTicket && this.bookingCode) {
          this.generateQr(this.yourTicket, this.bookingCode);
        }
        (this.extraTickets || []).forEach((extra) => {
          if (extra && extra.yourTicket && extra.bookingCode) {
            this.generateQr(extra.yourTicket, extra.bookingCode);
          }
        });
      }, 500);
    },
  },
};
</script>

<style scoped>
.success-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #f7f9fc;
  padding: 20px;
}

.success-card {
  background: #fff;
  border-radius: 16px;
  padding: 30px 20px;
  max-width: 520px;
  width: 100%;
  text-align: center;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
  animation: fadeIn 0.6s ease;
}

.icon-circle {
  width: 78px;
  height: 78px;
  margin: 0 auto 15px;
  border-radius: 50%;
  background: #4caf50;
  color: white;
  font-size: 42px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.success-title {
  font-size: 23px;
  font-weight: bold;
  margin-bottom: 5px;
  color: #222;
}

.success-subtitle {
  font-size: 16px;
  margin-bottom: 10px;
  color: #666;
}

.scan-note {
  font-size: 13px;
  color: #555;
  margin-bottom: 15px;
}

/* Single ticket layout: big QR on top (old style) */
.qr-section {
  margin: 10px 0 15px;
}
.qr-code {
  width: 140px;
  height: 140px;
  object-fit: contain;
}
.qr-section .booking-code-text {
  font-size: 14px;
  margin-top: 8px;
  color: #555;
}
.qr-section .payment-info {
  margin-top: 15px;
}

/* Ticket rows: repeats one per movie, QR inline with details */
.tickets-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 15px;
}

.ticket-row {
  display: flex;
  align-items: center;
  gap: 14px;
  text-align: left;
  background: #f9f9f9;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.qr-inline {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 90px;
}

.qr-code-sm {
  width: 80px;
  height: 80px;
  object-fit: contain;
}

.booking-code-text {
  font-size: 10px;
  margin-top: 4px;
  color: #555;
  text-align: center;
  word-break: break-all;
}

.ticket-details {
  flex: 1;
  min-width: 0;
  display: grid;
  gap: 4px;
}

.detail-row {
  display: grid;
  grid-template-columns: 55px 1fr;
  align-items: start;
  gap: 6px;
}

.detail-row .label {
  font-weight: 600;
  color: #444;
  font-size: 12px;
}

.detail-row .value {
  color: #222;
  font-weight: 500;
  font-size: 12px;
  word-break: break-word;
}

.payment-info {
  margin-top: 15px;
  text-align: left;
  font-size: 15px;
  background: #f9f9f9;
  padding: 12px;
  border-radius: 8px;
}

.final-summary-row {
  display: grid;
  grid-template-columns: 55px 1fr;
  align-items: center;
}

.final-summary .label {
  font-weight: 600;
  color: #444;
  font-size: 12px;
}

.final-summary .value {
  color: #222;
  font-weight: 500;
  font-size: 12px;
}

.final-amt-text {
  font-size: 18px;
  color: green;
  display: flex;
  justify-content: space-between;
  margin: 8px 0 0;
}

/* Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
